import { useEffect, useState } from 'react';
import { Modal, Button } from '@platform/ui';
import {
  subscriptionApi,
  type EarlyTerminationPreview,
  type SeatImpactPreview,
} from '../api/subscription.api';
import { useSubscriptionLifecycle } from '@/features/subscription';

interface CancelSubscriptionDialogProps {
  open: boolean;
  subscriptionId: string | null;
  /** 약정 구독이면 중도해지 정산 프리뷰를 조회해 예상 위약금을 보여준다. */
  isCommitment: boolean;
  /**
   * 기업 구독이면 좌석 영향도(이관/회수 인원)를 함께 조회한다.
   * 개인 구독은 좌석이 본인 1석뿐이라 의미가 없어 기본 false.
   */
  showSeatImpact?: boolean;
  onClose: () => void;
  onDone?: () => void;
}

const won = (n: number) => `${n.toLocaleString('ko-KR')}원`;

/**
 * 구독 해지 다이얼로그(§7.3/§7.8).
 * - 약정: 중도해지 정산 프리뷰(할인반환금+위약금) 표시 후 "해지 요청"(admin 승인·정산 대기).
 * - 무약정: 주기말 해지로 즉시 접수.
 * ‘취소·탈퇴 방해’ 금지 원칙상 만류/다단계 없이 1회 확인만 한다.
 */
export function CancelSubscriptionDialog({
  open,
  subscriptionId,
  isCommitment,
  showSeatImpact = false,
  onClose,
  onDone,
}: CancelSubscriptionDialogProps) {
  const { busy, requestCancellation } = useSubscriptionLifecycle();
  const [preview, setPreview] = useState<EarlyTerminationPreview | null>(null);
  const [impact, setImpact] = useState<SeatImpactPreview | null>(null);
  const [loading, setLoading] = useState(false);

  /**
   * 조회 대상 키. 닫히면 null.
   *
   * 대상이 바뀌는 순간 이전 결과를 버려야 한다 — 안 그러면 다른 구독의 위약금 금액이
   * 잠깐 그대로 보인다. 이 초기화는 렌더 중에 처리한다(useDerivedState 패턴).
   * effect 안에서 setState 하면 초기화 전 한 프레임이 이전 값으로 그려진다.
   */
  const fetchKey = open && subscriptionId ? subscriptionId : null;
  const [lastFetchKey, setLastFetchKey] = useState<string | null>(null);
  if (lastFetchKey !== fetchKey) {
    setLastFetchKey(fetchKey);
    setPreview(null);
    setImpact(null);
    setLoading(fetchKey !== null);
  }

  useEffect(() => {
    if (!fetchKey) return;
    let alive = true;
    // 위약금과 좌석 영향도를 함께 조회한다. 한쪽이 실패해도 나머지는 보여준다
    // (금액을 못 불러왔다고 "몇 명이 끊기는지"까지 가리면 더 위험하다).
    Promise.allSettled([
      isCommitment
        ? subscriptionApi.earlyTerminationPreview(fetchKey)
        : Promise.resolve(null),
      showSeatImpact
        ? subscriptionApi.seatImpact(fetchKey)
        : Promise.resolve(null),
    ])
      .then(([p, i]) => {
        if (!alive) return;
        setPreview(p.status === 'fulfilled' ? p.value : null);
        setImpact(i.status === 'fulfilled' ? i.value : null);
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [fetchKey, isCommitment, showSeatImpact]);

  /**
   * 실패 사유는 전역 mutation 에러 핸들러가 서버 메시지로 alert 한다.
   */
  const confirm = () => {
    if (!subscriptionId) return;
    requestCancellation.mutate(
      { id: subscriptionId },
      {
        onSuccess: () => {
          onDone?.();
          onClose();
        },
      },
    );
  };

  const orphaned = impact?.orphaned ?? 0;

  return (
    <Modal
      open={open}
      onClose={busy ? () => {} : onClose}
      title="구독 해지"
      description={
        isCommitment
          ? '약정 구독은 중도해지 시 정산금이 발생합니다.'
          : '현재 결제 주기가 끝나면 해지됩니다.'
      }
      size="md"
      closeOnBackdrop={!busy}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            돌아가기
          </Button>
          <Button variant="danger" onClick={confirm} disabled={busy || loading}>
            {busy ? '처리 중…' : isCommitment ? '해지 요청' : '해지하기'}
          </Button>
        </div>
      }
    >
      <div className="space-y-3 text-sm text-gray-700">
        {isCommitment ? (
          loading ? (
            <p className="text-gray-400">예상 정산금 계산 중…</p>
          ) : preview ? (
            <div className="rounded-lg bg-gray-50 p-4">
              <Row label="이용 개월" value={`${preview.usedMonths}개월`} />
              <Row label="잔여 개월" value={`${preview.remainingMonths}개월`} />
              <Row label="할인 반환금" value={won(preview.clawback)} />
              <ClawbackBreakdown preview={preview} />
              <Row label="위약금" value={won(preview.penalty)} />
              <div className="mt-2 flex justify-between border-t border-gray-200 pt-2 font-semibold text-gray-900">
                <span>예상 추가 청구</span>
                <span>{won(preview.total)}</span>
              </div>
              {/* 위약금만 보이면 "돈 내고 바로 끊긴다"로 읽힌다. 잔여 이용기간이
                  당월 요금의 대가라는 걸 같은 블록에서 못박는다. */}
              <p className="mt-3 border-t border-gray-200 pt-2 text-xs text-gray-500">
                이미 결제된 이번 달 요금은 환불되지 않으며, 현재 주기 만료일까지
                계속 이용할 수 있습니다.
              </p>
            </div>
          ) : (
            <p className="text-gray-400">정산금 정보를 불러오지 못했습니다.</p>
          )
        ) : (
          <p>
            해지해도 현재 주기 만료일까지는 계속 이용할 수 있습니다. 만료 시
            자동으로 종료됩니다.
          </p>
        )}

        {/*
          좌석 영향도 — 이 구조에서 가장 흔한 사고가 "금액만 보고 해지했는데
          현장 인원 접속이 끊기는" 것이라, 위약금보다 눈에 띄게 배치한다.
        */}
        {showSeatImpact && impact && (
          <div
            className={`rounded-lg p-4 ${
              orphaned > 0 ? 'bg-danger/10' : 'bg-gray-50'
            }`}
          >
            <p
              className={`text-sm font-semibold ${
                orphaned > 0 ? 'text-danger' : 'text-gray-900'
              }`}
            >
              {orphaned > 0
                ? `구성원 ${orphaned}명이 라이선스를 잃습니다`
                : '라이선스를 잃는 구성원은 없습니다'}
            </p>
            <div className="mt-2 space-y-0.5 text-xs text-gray-500">
              {impact.movable > 0 && (
                <p>· {impact.movable}명 — 다른 구독의 여유 좌석으로 자동 이관</p>
              )}
              {impact.covered > 0 && (
                <p>· {impact.covered}명 — 이미 다른 구독으로 보유 중(영향 없음)</p>
              )}
              {orphaned > 0 && (
                <p className="text-danger">
                  · {orphaned}명 — 여유 좌석이 없어 접속이 차단됩니다
                </p>
              )}
            </div>
            {orphaned > 0 && (
              <p className="mt-2 text-xs text-gray-500">
                해지 전에 다른 플랜의 좌석을 늘리면 자동으로 이관됩니다.
              </p>
            )}
          </div>
        )}

        <p className="text-xs text-gray-400">
          {isCommitment
            ? '해지 요청 후 운영자 승인 시 확정 위약금이 정산됩니다. 승인되어도 이미 결제된 이번 달 이용기간은 그대로 유지되며, 주기 만료일에 종료됩니다. 심사는 영업일 기준 최대 7일 소요될 수 있습니다.'
            : '언제든지 재구독할 수 있습니다.'}
        </p>
      </div>
    </Modal>
  );
}

/** 할인 반환금 내역 — 약정 할인분·점진 할인분. 0원인 항목은 감춘다. */
function ClawbackBreakdown({
  preview,
}: {
  preview: { clawbackCommitment?: number; clawbackProgressive?: number };
}) {
  const items = [
    ['약정 할인분', preview.clawbackCommitment],
    ['점진 할인분', preview.clawbackProgressive],
  ] as const;
  return (
    <>
      {items.map(([label, value]) =>
        value ? (
          <div key={label} className="flex justify-between py-0.5 pl-3 text-xs text-gray-400">
            <span>└ {label}</span>
            <span>{won(value)}</span>
          </div>
        ) : null,
      )}
    </>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between py-0.5">
      <span className="text-gray-500">{label}</span>
      <span>{value}</span>
    </div>
  );
}
